import {getFirstComment} from '../../../page-data/payloads/thread-previews'
import type {ConversationItem} from './build-conversation-items'
import {ownerAvatarPath} from '@github-ui/paths'
import {getCurrentUserLogin} from '@github-ui/client-env'

export interface AuthorFilterItem {
  login: string
  avatarUrl: string
  count: number
  isAgent?: boolean
}

type CurrentUser = Pick<AuthorFilterItem, 'login' | 'avatarUrl' | 'isAgent'>

export const getCurrentUser = () => {
  const login = getCurrentUserLogin()
  if (login) return {login, avatarUrl: ownerAvatarPath({owner: login}), isAgent: false}
}

const compareAuthors = (currentUser: CurrentUser | undefined) => (a: AuthorFilterItem, b: AuthorFilterItem) => {
  if (a.login === currentUser?.login) return -1
  if (b.login === currentUser?.login) return 1
  if (a.login === 'Copilot') return -1
  if (b.login === 'Copilot') return 1
  return a.login.localeCompare(b.login)
}

function getConversationItemAuthor(conversationItem: ConversationItem) {
  switch (conversationItem.type) {
    case 'issue-comment':
      return conversationItem.data.author ?? undefined
    case 'review-summary':
      return conversationItem.data.summary.author ?? undefined
    case 'thread':
      return getFirstComment(conversationItem.data)?.author
  }
}

/**
 * Builds a list of unique authors from issue comments.
 * Copilot authors are placed at the top of the list.
 */
export function getConversationItemsAuthors(
  items: ConversationItem[],
  countItems: ConversationItem[] = items,
): AuthorFilterItem[] {
  const currentUser = getCurrentUser()

  // Start with current user so they are always present in the list
  const authors = new Map<string, AuthorFilterItem>(
    currentUser ? [[currentUser.login, {...currentUser, count: 0}]] : [],
  )

  for (const item of items) {
    const author = getConversationItemAuthor(item)
    if (!author) continue

    const existingAuthor = authors.get(author.login)
    if (existingAuthor) {
      existingAuthor.isAgent = existingAuthor.isAgent === true && author.isAgent === true
    } else {
      authors.set(author.login, {
        login: author.login,
        avatarUrl: author.avatarUrl ?? '',
        count: 0,
        isAgent: author.isAgent ?? false,
      })
    }
  }

  for (const item of countItems) {
    const author = getConversationItemAuthor(item)
    if (!author) continue

    const entry = authors.get(author.login) ?? {
      login: author.login,
      avatarUrl: author.avatarUrl ?? '',
      count: 0,
      isAgent: author.isAgent ?? false,
    }
    entry.count++
    authors.set(author.login, entry)
  }

  return Array.from(authors.values()).sort(compareAuthors(currentUser))
}
